import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Edit, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProfiles = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [selectedType, setSelectedType] = useState('all');
  const [formData, setFormData] = useState({
    type: 'founder',
    name: '',
    title: '',
    photo_url: '',
    college: '',
    bio: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchProfiles();
  }, [selectedType]);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const url = selectedType === 'all' ? `${API}/profiles` : `${API}/profiles?type=${selectedType}`;
      const response = await axios.get(url);
      setProfiles(response.data);
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let photoUrl = formData.photo_url;
      
      if (selectedFile) {
        setUploadingImage(true);
        const uploadFormData = new FormData();
        uploadFormData.append('file', selectedFile);
        const uploadResponse = await axios.post(`${API}/upload`, uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        photoUrl = uploadResponse.data.url;
        setUploadingImage(false);
      }
      
      const profileData = {
        ...formData,
        photo_url: photoUrl
      };
      
      if (editingProfile) {
        await axios.put(`${API}/profiles/${editingProfile.id}`, profileData);
      } else {
        await axios.post(`${API}/profiles`, profileData);
      }
      fetchProfiles();
      resetForm();
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error saving profile. Please try again.');
      setUploadingImage(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this profile?')) {
      try {
        await axios.delete(`${API}/profiles/${id}`);
        fetchProfiles();
      } catch (error) {
        console.error('Error deleting profile:', error);
        alert('Error deleting profile. Please try again.');
      }
    }
  };

  const handleEdit = (profile) => {
    setEditingProfile(profile);
    setFormData({
      type: profile.type,
      name: profile.name,
      title: profile.title,
      photo_url: profile.photo_url,
      college: profile.college || '',
      bio: profile.bio
    });
    setImagePreview(profile.photo_url);
    setSelectedFile(null);
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingProfile(null);
    setFormData({
      type: 'founder',
      name: '',
      title: '',
      photo_url: '',
      college: '',
      bio: ''
    });
    setSelectedFile(null);
    setImagePreview(null);
    setUploadingImage(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="page-content">
      <section className="page-hero">
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <Link to="/admin/dashboard" className="btn-secondary" style={{ marginBottom: '16px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <ArrowLeft size={20} /> Back to Dashboard
              </Link>
              <h1 className="display-large">Manage Profiles</h1>
            </div>
            <button onClick={() => setShowForm(true)} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Plus size={20} /> Add Profile
            </button>
          </div>
        </div>
      </section>

      <section style={{ padding: 'var(--spacing-xl) var(--spacing-medium)' }}>
        <div className="container">
          {/* Type Filter */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ marginRight: '16px', fontWeight: 600 }}>Filter by Type:</label>
            <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} style={{ padding: '8px 16px', borderRadius: '8px', border: '2px solid var(--border-medium)' }}>
              <option value="all">All Profiles</option>
              <option value="founder">Founders</option>
              <option value="advisor">Advisors</option>
            </select>
          </div>

          {showForm && (
            <div className="network-card" style={{ marginBottom: '32px' }}>
              <h2 className="heading-2">{editingProfile ? 'Edit Profile' : 'Add New Profile'}</h2>
              <form onSubmit={handleSubmit} style={{ marginTop: '24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                  <div className="form-group">
                    <label>Type *</label>
                    <select value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})} required>
                      <option value="founder">Founder</option>
                      <option value="advisor">Advisor</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Name *</label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <label>Title *</label>
                    <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required placeholder="e.g., Founder & Director" />
                  </div>
                  {formData.type === 'advisor' && (
                    <div className="form-group">
                      <label>College</label>
                      <input type="text" value={formData.college} onChange={(e) => setFormData({...formData, college: e.target.value})} placeholder="e.g., UCLA" />
                    </div>
                  )}
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Biography *</label>
                    <textarea value={formData.bio} onChange={(e) => setFormData({...formData, bio: e.target.value})} required rows="4" placeholder="Biography and background information" />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Photo *</label>
                    <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={handleFileChange} style={{ marginBottom: '16px' }} />
                    {imagePreview && <div style={{ marginTop: '16px' }}><img src={imagePreview} alt="Preview" style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '12px', objectFit: 'cover', border: '2px solid var(--border-medium)' }} /></div>}
                    <p style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '8px' }}>Accepted formats: JPG, PNG, WebP (Max 5MB)</p>
                  </div>
                </div>
                <div style={{ marginTop: '24px', display: 'flex', gap: '16px' }}>
                  <button type="submit" className="btn-primary" disabled={uploadingImage}>{uploadingImage ? 'Uploading...' : (editingProfile ? 'Update Profile' : 'Add Profile')}</button>
                  <button type="button" onClick={resetForm} className="btn-secondary" disabled={uploadingImage}>Cancel</button>
                </div>
              </form>
            </div>
          )}

          {loading ? (
            <p>Loading profiles...</p>
          ) : (
            <div className="network-card">
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-medium)' }}>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Type</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Title</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>College</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profiles.length === 0 ? (
                      <tr><td colSpan="5" style={{ padding: '24px', textAlign: 'center' }}>No profiles found</td></tr>
                    ) : (
                      profiles.map(profile => (
                        <tr key={profile.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                          <td style={{ padding: '12px' }}>{profile.name}</td>
                          <td style={{ padding: '12px' }}><span style={{ textTransform: 'capitalize' }}>{profile.type}</span></td>
                          <td style={{ padding: '12px' }}>{profile.title}</td>
                          <td style={{ padding: '12px' }}>{profile.college || '-'}</td>
                          <td style={{ padding: '12px' }}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button onClick={() => handleEdit(profile)} style={{ padding: '6px 12px', background: 'var(--brand-primary)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Edit size={16} /> Edit
                              </button>
                              <button onClick={() => handleDelete(profile.id)} style={{ padding: '6px 12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Trash2 size={16} /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminProfiles;